import React, { useState, useEffect, useRef } from 'react';

const DocumentOrderSelector = ({
  onOrderChange,
  selectedTemplate,
  documentData,
  initialDocumentOrder = [],
}) => {
  // Define fallback options in case template data is not available
  const fallbackOptions = [];

  // Ref to track if initial setup has occurred
  const initialSetupDone = useRef(false);

  // Ref to track the previous consolidationApplicationNumber value
  const prevConsolidationApplicationNumber = useRef('');

  // Helper function to ensure MergeRequest or SingleTop is always at the top
  const ensureMergeRequestIsFirst = (options) => {
    if (!options || options.length === 0) return options;

    const mergeRequestIndex = options.findIndex(
      (opt) => opt.id === 'MergeRequest',
    );
    const singleTopIndex = options.findIndex(
      (opt) => getTemplateProperties(opt.id).singleTop,
    );

    // If MergeRequest exists and it's not at position 0, move it to the top
    if (mergeRequestIndex !== -1 && mergeRequestIndex !== 0) {
      const result = [...options];
      const [mergeRequest] = result.splice(mergeRequestIndex, 1);
      result.unshift(mergeRequest);
      return result;
    }

    // If SingleTop exists and it's not at position 0, move it to the top
    if (singleTopIndex !== -1 && singleTopIndex !== 0) {
      const result = [...options];
      const [singleTop] = result.splice(singleTopIndex, 1);
      result.unshift(singleTop);
      return result;
    }

    return options;
  };

  // Helper function to get template properties for an option
  const getTemplateProperties = (optionId) => {
    const titleObj = selectedTemplate?.titles?.[optionId];
    if (!titleObj || typeof titleObj !== 'object') {
      return {
        beforeTitleOtherwise: false,
        beforeResultOtherwise: false,
        singleTop: false,
      };
    }

    return {
      beforeTitleOtherwise: titleObj.beforeTitleOtherwise === true,
      beforeResultOtherwise: titleObj.beforeResultOtherwise === true,
      singleTop: optionId.includes('SingleTop'),
    };
  };

  // Helper function to check if an item can be placed at a specific position
  const canPlaceAtPosition = (draggedId, targetIndex, currentOptions) => {
    const draggedProps = getTemplateProperties(draggedId);

    // MergeRequest always stays at position 0
    if (draggedId === 'MergeRequest' && targetIndex !== 0) {
      console.log('DEBUG: MergeRequest cannot be moved from position 0');
      return false;
    }

    // SingleTop items always stay at position 0
    if (draggedProps.singleTop && targetIndex !== 0) {
      console.log('DEBUG: SingleTop item cannot be moved from position 0');
      return false;
    }

    // Nothing else can be placed at position 0 if MergeRequest exists
    const hasMergeRequest = currentOptions.some(
      (opt) => opt.id === 'MergeRequest',
    );
    if (hasMergeRequest && draggedId !== 'MergeRequest' && targetIndex === 0) {
      console.log(
        'DEBUG: Cannot place item at position 0 when MergeRequest exists',
      );
      return false;
    }

    // Nothing else can be placed at position 0 if SingleTop exists
    const hasSingleTop = currentOptions.some(
      (opt) => getTemplateProperties(opt.id).singleTop,
    );
    if (hasSingleTop && !draggedProps.singleTop && targetIndex === 0) {
      console.log(
        'DEBUG: Cannot place item at position 0 when SingleTop exists',
      );
      return false;
    }

    console.log(`DEBUG: Dragged item ${draggedId} properties:`, draggedProps);

    let beforeItem = currentOptions[targetIndex];
    if (beforeItem.id === draggedId && targetIndex > 0) {
      beforeItem = currentOptions[targetIndex - 1];
    } else if (beforeItem.id === draggedId && targetIndex === 0) {
      return true;
    }
    const beforeProps = getTemplateProperties(beforeItem.id);
    console.log(
      'Cannot after',
      beforeItem.id,
      beforeProps,
      draggedId,
      draggedProps,
      currentOptions,
    );
    if (
      draggedProps.beforeTitleOtherwise &&
      !beforeProps.beforeTitleOtherwise
    ) {
      console.log(
        `DEBUG: Cannot place ${draggedId} (beforeTitleOtherwise: ${draggedProps.beforeTitleOtherwise}) after ${beforeItem.id} (beforeTitleOtherwise: ${beforeProps.beforeTitleOtherwise})`,
      );
      return false;
    }
    if (
      draggedProps.beforeResultOtherwise &&
      !beforeProps.beforeResultOtherwise
    ) {
      console.log(
        `DEBUG: Cannot place ${draggedId} (beforeResultOtherwise: ${draggedProps.beforeResultOtherwise}) after ${beforeItem.id} (beforeResultOtherwise: ${beforeProps.beforeResultOtherwise})`,
      );
      return false;
    }
    let afterItem = currentOptions[targetIndex];
    if (afterItem.id === draggedId && targetIndex < currentOptions.length - 1) {
      afterItem = currentOptions[targetIndex + 1];
    } else if (
      afterItem.id === draggedId &&
      targetIndex === currentOptions.length - 1
    ) {
      return true;
    }
    const afterProps = getTemplateProperties(afterItem.id);
    console.log(
      'Cannot before',
      afterItem.id,
      afterProps,
      draggedId,
      draggedProps,
    );
    if (!draggedProps.beforeTitleOtherwise && afterProps.beforeTitleOtherwise) {
      console.log(
        `DEBUG: Cannot place ${draggedId} (beforeTitleOtherwise: ${draggedProps.beforeTitleOtherwise}) before ${afterItem.id} (beforeTitleOtherwise: ${afterProps.beforeTitleOtherwise})`,
      );
      return false;
    }
    if (
      !draggedProps.beforeResultOtherwise &&
      afterProps.beforeResultOtherwise
    ) {
      console.log(
        `DEBUG: Cannot place ${draggedId} (beforeResultOtherwise: ${draggedProps.beforeResultOtherwise}) before ${afterItem.id} (beforeResultOtherwise: ${afterProps.beforeResultOtherwise})`,
      );
      return false;
    }
    return true;
  };

  // Helper function to find the nearest valid position for an item
  const findNearestValidPosition = (draggedId, targetIndex, currentOptions) => {
    console.log(
      `DEBUG: Finding nearest valid position for ${draggedId} near index ${targetIndex}`,
    );

    const draggedProps = getTemplateProperties(draggedId);

    // If dragged item is SingleTop, it can only be at position 0
    if (draggedProps.singleTop) {
      console.log(`DEBUG: SingleTop item can only be at position 0`);
      return 0;
    }

    // Start from the target position and expand outward
    const maxIndex = currentOptions.length;

    // Try the exact target position first
    if (canPlaceAtPosition(draggedId, targetIndex, currentOptions)) {
      console.log(`DEBUG: Exact target position ${targetIndex} is valid`);
      return targetIndex;
    }

    // Expand outward from the target position
    for (let offset = 1; offset < maxIndex; offset++) {
      // Try positions to the left
      const leftPos = targetIndex - offset;
      if (
        leftPos >= 0 &&
        canPlaceAtPosition(draggedId, leftPos, currentOptions)
      ) {
        console.log(`DEBUG: Found valid position to the left: ${leftPos}`);
        return leftPos;
      }

      // Try positions to the right
      const rightPos = targetIndex + offset;
      if (
        rightPos < maxIndex &&
        canPlaceAtPosition(draggedId, rightPos, currentOptions)
      ) {
        console.log(`DEBUG: Found valid position to the right: ${rightPos}`);
        return rightPos;
      }
    }

    console.log(`DEBUG: No valid position found, keeping current position`);
    return currentOptions.findIndex((opt) => opt.id === draggedId);
  };

  // Get all available options
  const [allOptions, setAllOptions] = useState(fallbackOptions);

  // State for selected and available options
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);

  // Generate options from template titles if available
  const getOptionsFromTemplate = () => {
    if (selectedTemplate && selectedTemplate.titles) {
      const titleEntries = Object.entries(selectedTemplate.titles || {});
      console.log('Template titles found:', titleEntries.length);

      if (titleEntries.length === 0) {
        console.log('No titles found in template, using fallback options');
        return fallbackOptions;
      }

      // Map entries to option objects with order property
      const options = titleEntries.map(([id, titleObj]) => {
        // Handle case where titleObj is not an object
        if (typeof titleObj !== 'object' || titleObj === null) {
          console.log(`Invalid title object for ${id}:`, titleObj);
          return { id, name: id, order: 999 };
        }

        return {
          id: id,
          name: titleObj.name || id,
          order: typeof titleObj.order === 'number' ? titleObj.order : 999, // Default high value for items without order
        };
      });

      // Special handling: Always put MergeRequest at the top (order 0)
      const sortedOptions = options
        .map((option) => {
          if (option.id === 'MergeRequest') {
            return { ...option, order: 0 }; // Assign order 0 to MergeRequest to ensure it comes first
          }
          return option;
        })
        .sort((a, b) => a.order - b.order);

      console.log('Sorted template options by order:', sortedOptions);

      // Return sorted options without the order property in the final result
      return sortedOptions.map(({ id, name }) => ({ id, name }));
    }

    console.log('Using fallback options, no template or titles available');
    return fallbackOptions;
  };

  // Update options when the template changes OR when an initial order is
  // provided from outside (pre-created document flow).
  useEffect(() => {
    if (
      !selectedTemplate ||
      !selectedTemplate.titles ||
      Object.keys(selectedTemplate.titles || {}).length === 0
    ) {
      if (initialSetupDone.current) {
        // We already initialised once – ignore this spurious update
        console.warn(
          'DocumentOrderSelector: Received template without titles after initial setup. Ignoring to preserve current order.',
        );
        return;
      }

      // Still in the very first initialisation -> fall back as before
      console.log('No template selected, using fallback options');
      const fallbackOpts = fallbackOptions;
      setAllOptions(fallbackOpts);
      setSelectedOptions(ensureMergeRequestIsFirst(fallbackOpts));
      setAvailableOptions([]);
      return;
    }

    // From here on, we are guaranteed to have a template WITH titles

    // Debug logging for template titles
    console.log('Template received:', selectedTemplate);

    if (selectedTemplate.titles) {
      console.log('Template titles with order values:');
      try {
        Object.entries(selectedTemplate.titles).forEach(([id, titleObj]) => {
          console.log(
            `Title: ${id}, Name: ${titleObj?.name}, Order: ${titleObj?.order}`,
          );
        });
      } catch (err) {
        console.error('Error processing template titles:', err);
      }
    } else {
      console.log('No titles found in template');
    }

    try {
      const templateOptions = getOptionsFromTemplate();
      console.log('Template options after sorting:', templateOptions);

      setAllOptions(templateOptions);

      // Check if consolidationApplicationNumber is empty and exclude MergeRequest if so
      const consolidationNumber =
        documentData?.consolidationApplicationNumber || '';
      const shouldIncludeMergeRequest = consolidationNumber.trim() !== '';

      let initialSelectedOptions;
      let initialAvailableOptions;

      // -----------------------------
      // 1) If parent supplied a saved order, honour it first
      // -----------------------------
      if (Array.isArray(initialDocumentOrder) && initialDocumentOrder.length) {
        // Convert incoming items (which might be strings or objects) to the
        // {id, name} shape expected by this component.
        const resolvedSelected = initialDocumentOrder
          .map((item) => {
            if (!item) return null;

            // Item might be just an id string
            if (typeof item === 'string') {
              const match = templateOptions.find((opt) => opt.id === item);
              return match || { id: item, name: item };
            }

            // Or it might already be an object with id/name
            if (typeof item === 'object' && item.id) {
              // Ensure `name` is filled in (may have come back undefined)
              if (!item.name) {
                const match = templateOptions.find((opt) => opt.id === item.id);
                return match || { id: item.id, name: item.id };
              }
              return { id: item.id, name: item.name };
            }

            return null;
          })
          .filter(Boolean);

        // Filter out SingleTop options that shouldn't be selected by default
        const filteredSelected = resolvedSelected.filter((item) => {
          const props = getTemplateProperties(item.id);
          // Only include SingleTop if it was explicitly in the saved order
          return (
            !props.singleTop ||
            resolvedSelected.some((selected) => selected.id === item.id)
          );
        });

        initialSelectedOptions = ensureMergeRequestIsFirst(filteredSelected);

        // Anything not selected becomes available
        const selectedIds = new Set(initialSelectedOptions.map((o) => o.id));
        initialAvailableOptions = templateOptions.filter(
          (opt) => !selectedIds.has(opt.id),
        );
      } else {
        // -----------------------------
        // 2) No saved order – fall back to existing logic
        // -----------------------------

        // Separate SingleTop options from other options
        const singleTopOptions = templateOptions.filter(
          (opt) => getTemplateProperties(opt.id).singleTop,
        );

        if (shouldIncludeMergeRequest) {
          initialSelectedOptions = ensureMergeRequestIsFirst(
            templateOptions.filter(
              (opt) => !getTemplateProperties(opt.id).singleTop,
            ),
          );
          initialAvailableOptions = singleTopOptions;
        } else {
          // Exclude MergeRequest and SingleTop from initial selection
          const optionsWithoutMergeRequestAndSingleTop = templateOptions.filter(
            (opt) =>
              opt.id !== 'MergeRequest' &&
              !getTemplateProperties(opt.id).singleTop,
          );
          const mergeRequestOption = templateOptions.find(
            (opt) => opt.id === 'MergeRequest',
          );

          initialSelectedOptions = optionsWithoutMergeRequestAndSingleTop;
          initialAvailableOptions = [
            ...(mergeRequestOption ? [mergeRequestOption] : []),
            ...singleTopOptions,
          ];
        }
      }

      setSelectedOptions(initialSelectedOptions);
      setAvailableOptions(initialAvailableOptions);

      // Notify parent component only if this isn't the initial setup
      if (initialSetupDone.current && typeof onOrderChange === 'function') {
        onOrderChange(initialSelectedOptions);
      } else {
        // Mark initial setup as done
        initialSetupDone.current = true;
        // Set initial consolidationApplicationNumber value
        prevConsolidationApplicationNumber.current = consolidationNumber;
      }
    } catch (err) {
      console.error('Error processing template options:', err);
      // Fall back to default options
      setAllOptions(fallbackOptions);
      setSelectedOptions(ensureMergeRequestIsFirst(fallbackOptions));
      setAvailableOptions([]);
    }
  }, [selectedTemplate]); // Run when template changes only

  // NOTE: We deliberately omit `initialDocumentOrder` from the dependency array
  // to avoid an update loop when this component notifies the parent, which then
  // passes a new (but referentially different) array back down. We only care
  // about the initially supplied order when the template changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Handle consolidationApplicationNumber changes
  useEffect(() => {
    if (!documentData || !initialSetupDone.current) {
      return;
    }

    const currentConsolidationNumber =
      documentData.consolidationApplicationNumber || '';
    const previousConsolidationNumber =
      prevConsolidationApplicationNumber.current;

    // Check if consolidationApplicationNumber has changed
    if (currentConsolidationNumber !== previousConsolidationNumber) {
      console.log('ConsolidationApplicationNumber changed:', {
        previous: previousConsolidationNumber,
        current: currentConsolidationNumber,
      });

      const mergeRequestOption = allOptions.find(
        (opt) => opt.id === 'MergeRequest',
      );
      if (!mergeRequestOption) {
        console.log('MergeRequest option not found in allOptions');
        prevConsolidationApplicationNumber.current = currentConsolidationNumber;
        return;
      }

      const isMergeRequestSelected = selectedOptions.some(
        (opt) => opt.id === 'MergeRequest',
      );
      const shouldMergeRequestBeSelected =
        currentConsolidationNumber.trim() !== '';

      if (shouldMergeRequestBeSelected && !isMergeRequestSelected) {
        // Add MergeRequest to selected options
        console.log('Adding MergeRequest to selected options');

        // If there's a SingleTop selected, remove it first
        const existingSingleTop = selectedOptions.find(
          (opt) => getTemplateProperties(opt.id).singleTop,
        );

        let updatedSelected = selectedOptions.filter(
          (opt) =>
            opt.id !== 'MergeRequest' &&
            !getTemplateProperties(opt.id).singleTop,
        );
        updatedSelected = [mergeRequestOption, ...updatedSelected];

        let updatedAvailable = availableOptions.filter(
          (opt) => opt.id !== 'MergeRequest',
        );

        // Add SingleTop to available options if it was selected
        if (existingSingleTop) {
          updatedAvailable = [...updatedAvailable, existingSingleTop];
        }

        setSelectedOptions(updatedSelected);
        setAvailableOptions(updatedAvailable);
      } else if (!shouldMergeRequestBeSelected && isMergeRequestSelected) {
        // Remove MergeRequest from selected options
        console.log('Removing MergeRequest from selected options');
        const updatedSelected = selectedOptions.filter(
          (opt) => opt.id !== 'MergeRequest',
        );
        const updatedAvailable = [...availableOptions, mergeRequestOption];

        setSelectedOptions(updatedSelected);
        setAvailableOptions(updatedAvailable);
      }

      // Update the previous value
      prevConsolidationApplicationNumber.current = currentConsolidationNumber;
    }
  }, [documentData, allOptions, selectedOptions, availableOptions]);

  // Notify parent when selectedOptions change, but only after initial setup
  useEffect(() => {
    if (
      initialSetupDone.current &&
      selectedOptions.length > 0 &&
      typeof onOrderChange === 'function'
    ) {
      // Always ensure MergeRequest is first before notifying parent
      const orderedOptions = ensureMergeRequestIsFirst(selectedOptions);
      onOrderChange(orderedOptions);
    }
  }, [selectedOptions]); // Only depend on selectedOptions

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const handleSelectOption = (optionId) => {
    // Find and remove the option from available options
    const option = availableOptions.find((opt) => opt.id === optionId);
    if (!option) return;

    setAvailableOptions(availableOptions.filter((opt) => opt.id !== optionId));

    // Get both template properties
    const templateProps = getTemplateProperties(optionId);
    console.log(
      `DEBUG: Adding option ${optionId} with properties:`,
      templateProps,
    );

    let updatedOptions = [...selectedOptions];
    let updatedAvailableOptions = [
      ...availableOptions.filter((opt) => opt.id !== optionId),
    ];

    // Handle SingleTop: remove existing SingleTop and add it to available options
    if (templateProps.singleTop) {
      const existingSingleTop = updatedOptions.find(
        (opt) => getTemplateProperties(opt.id).singleTop,
      );
      if (existingSingleTop) {
        // Remove existing SingleTop from selected options
        updatedOptions = updatedOptions.filter(
          (opt) => opt.id !== existingSingleTop.id,
        );
        // Add existing SingleTop to available options
        updatedAvailableOptions = [
          ...updatedAvailableOptions,
          existingSingleTop,
        ];
      }
      // Add new SingleTop to the beginning
      updatedOptions = [option, ...updatedOptions];
      setAvailableOptions(updatedAvailableOptions);
    } else if (templateProps.beforeTitleOtherwise === true) {
      // Add to the beginning (but after MergeRequest/SingleTop if it exists)
      const hasMergeRequest = updatedOptions.some(
        (opt) => opt.id === 'MergeRequest',
      );
      const hasSingleTop = updatedOptions.some(
        (opt) => getTemplateProperties(opt.id).singleTop,
      );

      if (hasMergeRequest) {
        // Keep MergeRequest first, then add the new option, then the rest
        const mergeRequest = updatedOptions.find(
          (opt) => opt.id === 'MergeRequest',
        );
        const otherOptions = updatedOptions.filter(
          (opt) => opt.id !== 'MergeRequest',
        );
        updatedOptions = [mergeRequest, option, ...otherOptions];
      } else if (hasSingleTop) {
        // Keep SingleTop first, then add the new option, then the rest
        const singleTop = updatedOptions.find(
          (opt) => getTemplateProperties(opt.id).singleTop,
        );
        const otherOptions = updatedOptions.filter(
          (opt) => opt.id !== singleTop.id,
        );
        updatedOptions = [singleTop, option, ...otherOptions];
      } else {
        // No MergeRequest or SingleTop, add to the very beginning
        updatedOptions = [option, ...updatedOptions];
      }
    } else {
      // Add to the end (default behavior)
      updatedOptions = [...updatedOptions, option];
    }

    setSelectedOptions(ensureMergeRequestIsFirst(updatedOptions));
  };

  const handleRemoveOption = (optionId) => {
    // Find the option to be removed
    const optionToRemove = selectedOptions.find(
      (option) => option.id === optionId,
    );

    if (!optionToRemove) return;

    // Add the removed option back to available options
    setAvailableOptions((prev) => [...prev, optionToRemove]);

    // Create updated selectedOptions
    const updatedSelectedOptions = selectedOptions.filter(
      (option) => option.id !== optionId,
    );

    // Update state with filtered selection
    setSelectedOptions(ensureMergeRequestIsFirst(updatedSelectedOptions));
  };

  const handleDragStart = (e, id) => {
    setDraggedItem(id);
    e.dataTransfer.setData('text/plain', id);
    // Add a dragging class to the element for visual feedback
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    // Remove the dragging class
    e.target.classList.remove('dragging');
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();

    // Only update if we're dragging over a different item
    if (draggedItem !== id && dragOverItem !== id) {
      setDragOverItem(id);

      // Real-time reordering as we drag over items
      if (draggedItem !== null) {
        const reordered = [...selectedOptions];
        const draggedIndex = reordered.findIndex(
          (opt) => opt.id === draggedItem,
        );
        const targetIndex = reordered.findIndex((opt) => opt.id === id);

        if (draggedIndex !== -1 && targetIndex !== -1) {
          console.log(
            `DEBUG: Attempting to move ${draggedItem} from index ${draggedIndex} to index ${targetIndex}`,
          );

          // Find the nearest valid position
          const validTargetIndex = findNearestValidPosition(
            draggedItem,
            targetIndex,
            reordered,
          );

          if (validTargetIndex !== draggedIndex) {
            // Remove the dragged item
            const [removed] = reordered.splice(draggedIndex, 1);
            // Insert at the valid position
            reordered.splice(validTargetIndex, 0, removed);

            console.log(
              `DEBUG: Successfully moved ${draggedItem} to valid position ${validTargetIndex}`,
            );

            // Update state and notify parent
            setSelectedOptions(ensureMergeRequestIsFirst(reordered));
          } else {
            console.log(`DEBUG: No valid move available for ${draggedItem}`);
          }
        }
      }
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    // We don't need to reorder again here since we're already doing it in dragOver
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Add early return: hide component when there are no titles to display
  if (
    !selectedTemplate ||
    !selectedTemplate.titles ||
    Object.keys(selectedTemplate.titles).length === 0
  ) {
    return null; // Do not render the selector when there are no titles
  }

  return (
    <div className="document-order-selector">
      <h3 className="document-order-title">Başlık Sıralaması</h3>
      <div className="document-options-container">
        {availableOptions.map((option) => (
          <button
            key={option.id}
            className="document-option-button"
            onClick={() => handleSelectOption(option.id)}
          >
            {option.name}
          </button>
        ))}
      </div>

      <div className="selected-documents">
        <ul className="document-list">
          {selectedOptions.map((option) => (
            <li
              key={option.id}
              draggable
              onDragStart={(e) => handleDragStart(e, option.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, option.id)}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`document-item ${
                dragOverItem === option.id ? 'drag-over' : ''
              } ${draggedItem === option.id ? 'dragging' : ''}`}
            >
              <span className="document-name">{option.name}</span>
              <span className="drag-handle" aria-label="Drag to reorder">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="10"
                    y="12"
                    width="20"
                    height="4"
                    rx="2"
                    fill="#888"
                  />
                  <rect
                    x="10"
                    y="19"
                    width="20"
                    height="4"
                    rx="2"
                    fill="#888"
                  />
                  <rect
                    x="10"
                    y="26"
                    width="20"
                    height="4"
                    rx="2"
                    fill="#888"
                  />
                </svg>
              </span>
              <button
                className="remove-document"
                onClick={() => handleRemoveOption(option.id)}
                aria-label={`Remove ${option.name}`}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="16" cy="16" r="15" fill="#5772a3" />
                  <path
                    d="M21 11L11 21M11 11L21 21"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DocumentOrderSelector;
